import { BackLink } from '@bp/ui-components';
import { Fragment, memo } from 'react';
import { Link, useMatches } from 'react-router-dom';
import styles from './Breadcrumb.module.scss';

export type CrumbItemType = {
  slug: string;
  title: string;
  asHeadline?: boolean;
};

export type CrumbType = {
  crumb: (data: unknown) => CrumbItemType | CrumbItemType[];
};

type BreadcrumbProps = {
  /** Client-Navigation für den mobilen Zurück-Link (Navigation gehört nicht in components/) */
  onNavigate: (path: string) => void;
};

export const Breadcrumb = memo(({ onNavigate }: BreadcrumbProps) => {
  const matches = useMatches();

  const crumbs = matches
    .filter((match) => {
      // @ts-ignore
      return Boolean(match.handle?.crumb); // How to overwrite crumb ? see src/types/react-router.d.ts
    })
    .map((match) => {
      const handle = match.handle as CrumbType;
      return handle.crumb(match.data);
    });

  // Mobil ersetzt eine Overline mit der übergeordneten Ebene die Breadcrumb (Zurück-Link)
  const flatCrumbs = crumbs.flat();
  const parent = flatCrumbs.length > 1 ? flatCrumbs[flatCrumbs.length - 2] : undefined;
  // Crumb-Slugs sind ohne führenden „/“ – für den echten href (neuer Tab) absolut machen
  const parentHref = parent?.slug ? (parent.slug.startsWith('/') ? parent.slug : `/${parent.slug}`) : undefined;

  return (
    <>
      <div className={styles.overline}>
        {parent && <BackLink label={parent.title} href={parentHref} onNavigate={onNavigate} />}
      </div>
      <div className={styles.breadcrumb}>
        {crumbs.map((crumb, index) => (
          <Fragment key={index}>
            {Array.isArray(crumb) ? (
              crumb.map((c, j) => (
                <div key={`${index}${j}`} className={styles.link}>
                  {c.slug && c.slug !== '' ? (
                    <Link key={c.slug} to={c.slug}>
                      {c.title}
                    </Link>
                  ) : (
                    <span>{c.title}</span>
                  )}
                </div>
              ))
            ) : (
              <div className={styles.link}>
                {crumb.slug && crumb.slug !== '' ? (
                  <Link key={crumb.slug} to={crumb.slug}>
                    {crumb.title}
                  </Link>
                ) : (
                  <span>{crumb.title}</span>
                )}
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </>
  );
});
Breadcrumb.displayName = 'Breadcrumb';
